import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Image as ImageIcon, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { AdminSection } from "@/components/admin/AdminSection";
import { MediaField } from "@/components/admin/MediaField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { listBanners, saveBanner, deleteBanner } from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/banners")({
  component: BannersAdminPage,
});

type BannerRow = {
  id: string;
  page_key: string;
  title: string | null;
  subtitle: string | null;
  image_url: string | null;
  cta_text: string | null;
  cta_link: string | null;
  display_order: number;
  is_published: boolean;
  publish_at: string | null;
  unpublish_at: string | null;
};

type Draft = {
  id?: string;
  page_key: string;
  title: string;
  subtitle: string;
  image_url: string;
  cta_text: string;
  cta_link: string;
  display_order: number;
  is_published: boolean;
  publish_at: string;
  unpublish_at: string;
};

const empty = (order: number): Draft => ({
  page_key: "home",
  title: "",
  subtitle: "",
  image_url: "",
  cta_text: "",
  cta_link: "",
  display_order: order,
  is_published: true,
  publish_at: "",
  unpublish_at: "",
});

const toLocalInput = (iso: string | null) => (iso ? iso.slice(0, 16) : "");

function BannersAdminPage() {
  const list = useServerFn(listBanners);
  const save = useServerFn(saveBanner);
  const remove = useServerFn(deleteBanner);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "banners"], queryFn: () => list() });
  const rows = useMemo(
    () => ((data ?? []) as unknown as BannerRow[]).sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "banners"] });
    void qc.invalidateQueries({ queryKey: ["cms"] });
  };

  const saveMutation = useMutation({
    mutationFn: (d: Draft) =>
      save({
        data: {
          ...d,
          title: d.title || null,
          subtitle: d.subtitle || null,
          image_url: d.image_url || null,
          cta_text: d.cta_text || null,
          cta_link: d.cta_link || null,
          publish_at: d.publish_at ? new Date(d.publish_at).toISOString() : null,
          unpublish_at: d.unpublish_at ? new Date(d.unpublish_at).toISOString() : null,
        } as never,
      }),
    onSuccess: () => { toast.success("Banner saved"); setDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => { toast.success("Banner removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the banner."),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Banners"
        description="Scheduled promotional banners for public pages, with optional call-to-action links."
        actions={<ImportButtons moduleId="banners" />}
      />
      <div className="flex justify-end">
        <Button size="sm" className="gap-1" onClick={() => setDraft(empty(rows.length))}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add banner
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading banners…</p> : null}

      <AdminSection number={1} icon={ImageIcon} title="All banners" description="Grouped by the page they appear on.">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No banners yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {rows.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                {b.image_url ? (
                  <img src={b.image_url} alt="" className="h-10 w-16 rounded object-cover" loading="lazy" />
                ) : (
                  <div className="h-10 w-16 rounded bg-muted" />
                )}
                <div className="min-w-40 flex-1">
                  <p className="text-sm font-medium text-foreground">{b.title || "Untitled banner"}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.page_key}
                    {b.publish_at ? ` · from ${new Date(b.publish_at).toLocaleDateString("en-IN")}` : ""}
                    {b.unpublish_at ? ` · until ${new Date(b.unpublish_at).toLocaleDateString("en-IN")}` : ""}
                  </p>
                </div>
                {!b.is_published ? (
                  <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">Draft</span>
                ) : null}
                <div className="ml-auto flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      setDraft({
                        id: b.id,
                        page_key: b.page_key,
                        title: b.title ?? "",
                        subtitle: b.subtitle ?? "",
                        image_url: b.image_url ?? "",
                        cta_text: b.cta_text ?? "",
                        cta_link: b.cta_link ?? "",
                        display_order: b.display_order,
                        is_published: b.is_published,
                        publish_at: toLocalInput(b.publish_at),
                        unpublish_at: toLocalInput(b.unpublish_at),
                      })
                    }
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost">
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes "{b.title || "Untitled banner"}" permanently. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMutation.mutate(b.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AdminSection>

      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit banner" : "Add banner"}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ban-page" className="text-xs">Page key</Label>
                <Input id="ban-page" value={draft.page_key} onChange={(e) => setDraft({ ...draft, page_key: e.target.value })} placeholder="home" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ban-title" className="text-xs">Title</Label>
                <Input id="ban-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="ban-sub" className="text-xs">Subtitle</Label>
                <Input id="ban-sub" value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Image</Label>
                <MediaField value={draft.image_url} onChange={(v) => setDraft({ ...draft, image_url: v })} allowUrlInput />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ban-cta" className="text-xs">CTA text</Label>
                <Input id="ban-cta" value={draft.cta_text} onChange={(e) => setDraft({ ...draft, cta_text: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ban-ctalink" className="text-xs">CTA link</Label>
                <Input id="ban-ctalink" value={draft.cta_link} onChange={(e) => setDraft({ ...draft, cta_link: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ban-from" className="text-xs">Publish from</Label>
                <Input id="ban-from" type="datetime-local" value={draft.publish_at} onChange={(e) => setDraft({ ...draft, publish_at: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ban-until" className="text-xs">Publish until</Label>
                <Input id="ban-until" type="datetime-local" value={draft.unpublish_at} onChange={(e) => setDraft({ ...draft, unpublish_at: e.target.value })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
                <span className="text-sm">Published</span>
                <Switch checked={draft.is_published} onCheckedChange={(v) => setDraft({ ...draft, is_published: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button disabled={!draft?.page_key.trim() || saveMutation.isPending} onClick={() => draft && saveMutation.mutate(draft)}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
